import { useMemo, useContext, createContext, useCallback, useEffect } from 'react'
import { useDispatch, useSelector, useSelectorWithRef } from '../../../redux/hooks'
import companyCardsActions from '../../../redux/modules/companyCards/actions'
import companyCardsSelectors from '../../../redux/modules/companyCards/selectors'
import authSelectors from '../../../redux/modules/auth/selectors'
import { useUnsubscribe } from '../../../hooks/shared/useUnsubscribe'
import { CompanyCard } from '@leadgen/models/client'
import { useRegisteredComponents } from '../../../hooks/shared/useRegisteredComponents'
import useStableCallback from '../../../hooks/shared/useStableCallback'

export type CompanyCardsStatus =
  | 'reloading'
  | 'loading'
  | 'idle'
  | 'empty'
  | 'error'
  | 'error-reloading'

const CompanyCardsContext = createContext<{
  registerComponent: () => () => void
  loading: boolean
  load: () => void
  status: CompanyCardsStatus
  companyCards: CompanyCard[]
  error: string | undefined
} | null>(null)

const useCompanyCards = (
  { skipRegistration }: { skipRegistration: boolean } = { skipRegistration: false },
) => {
  const context = useContext(CompanyCardsContext)
  if (!context) {
    throw new Error('no CompanyCardsProvider found when calling useCompanyCards()')
  }
  const { registerComponent, load, loading, status, companyCards, error } = context
  useEffect(() => {
    if (!skipRegistration) {
      return registerComponent()
    }
  }, [registerComponent, skipRegistration])
  return {
    load,
    loading,
    status,
    companyCards,
    error,
  }
}

const CompanyCardsProvider = ({ children }) => {
  const dispatch = useDispatch()
  const { unsubscribe, registerUnsubscribe } = useUnsubscribe()
  const { registerComponent, hasRegisteredComponents, registeredComponentsRef } =
    useRegisteredComponents()
  const companyCards = useSelector(companyCardsSelectors.getCompanyCards)
  const [loading, loadingRef] = useSelectorWithRef(companyCardsSelectors.getLoading())
  const [, initialRef] = useSelectorWithRef(companyCardsSelectors.getInitial)
  const error = useSelector(companyCardsSelectors.getError)
  const companyId = useSelector(authSelectors.getCompanyId)
  const status: CompanyCardsStatus =
    loading && companyCards.length
      ? 'reloading'
      : loading
        ? 'loading'
        : error
          ? companyCards.length
            ? 'error-reloading'
            : 'error'
          : companyCards.length
            ? 'idle'
            : 'empty'
  const load = useCallback(() => {
    if (companyId && hasRegisteredComponents && (!loadingRef.current || initialRef.current)) {
      dispatch(companyCardsActions.loadCompanyCards({ companyId }))
        .unwrap()
        .then(({ unsubscribe: unsubscribeFromCompanyCards }) => {
          if (!registeredComponentsRef.current.length) {
            unsubscribeFromCompanyCards()
            unsubscribe()
          } else {
            unsubscribe()
            registerUnsubscribe(unsubscribeFromCompanyCards)
          }
        })
    }
  }, [
    companyId,
    dispatch,
    hasRegisteredComponents,
    initialRef,
    loadingRef,
    registerUnsubscribe,
    registeredComponentsRef,
    unsubscribe,
  ])
  useEffect(() => {
    load()
  }, [load])
  useEffect(() => {
    if (!hasRegisteredComponents) {
      unsubscribe()
      return
    }
  }, [hasRegisteredComponents, unsubscribe])
  useEffect(() => {
    return () => {
      unsubscribe()
    }
  }, [unsubscribe])
  const loadHandler = useStableCallback(load)
  const value = useMemo(
    () => ({
      registerComponent,
      loading,
      load: loadHandler,
      status,
      companyCards,
      error,
    }),
    [registerComponent, loading, loadHandler, status, companyCards, error],
  )
  return <CompanyCardsContext.Provider value={value}>{children}</CompanyCardsContext.Provider>
}

export { useCompanyCards, CompanyCardsProvider }
export default CompanyCardsProvider
